import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, ClipboardList, Clock, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api'
import {
  useAssignableUsers,
  useCreateTaskOnSubmission,
  useMyTasks,
  useSubmissions,
  useUpdateTask,
} from '@/lib/queries'
import { useAuth } from '@/context/AuthContext'
import {
  Badge,
  Button,
  Card,
  DateInput,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  todayISO,
} from '@/components/ui'
import { HelpNote } from '@/components/Hint'
import { formatDate } from '@/lib/utils'
import { useLinkedParam } from '@/lib/useLinkedParam'
import { NEW_TASK_PARAM } from '@/components/shell/navigation'
import { useT } from '@/i18n/I18nProvider'
import type { SubmissionStatusValue, Task } from '@/types/api'

/** Everything not yet resolved. Assigning work to a closed report is a mistake. */
const OPEN_STATUSES: SubmissionStatusValue[] = ['NEW', 'REVIEW', 'ASSIGNED', 'IN_PROGRESS']

/**
 * "What am I supposed to do today."
 *
 * Sorted by deadline with undated work last, and overdue items called out —
 * this is a list someone checks at the start of a shift, so the ordering is the
 * feature.
 */
export function TasksPage() {
  const { user } = useAuth()
  const t = useT()
  const [openOnly, setOpenOnly] = useState(true)
  const query = useMyTasks(openOnly)

  const tasks = query.data?.data ?? []
  const overdue = tasks.filter((task) => task.isOverdue)

  // Only managers may assign work, and the API says so too — this just avoids
  // offering a button that would come back 403.
  const canCreate = user?.isManager === true

  /*
   * `?novi=1` opens the form straight away — it is where "+ Novo › Novi
   * zadatak" leads from anywhere in the app. The parameter is dropped once the
   * form closes, so a reload or a back button does not open it a second time.
   */
  const [, setSearchParams] = useSearchParams()
  const linkedNew = useLinkedParam(NEW_TASK_PARAM, () => setCreating(canCreate))
  const [creating, setCreating] = useState(canCreate && linkedNew !== null)

  function closeNewTask() {
    setCreating(false)
    setSearchParams(
      (params) => {
        params.delete(NEW_TASK_PARAM)
        return params
      },
      { replace: true },
    )
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{t('tasks.title')}</h1>
          <p className="muted text-sm">
            {openOnly ? t('tasks.subtitle.openOnly') : t('tasks.subtitle.all')}
            {query.data && ` · ${query.data.meta.total}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setOpenOnly((value) => !value)}>
            {openOnly ? t('tasks.filter.showAll') : t('tasks.filter.openOnly')}
          </Button>

          {canCreate && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              {t('tasks.new')}
            </Button>
          )}
        </div>
      </header>

      {/*
        Above the list, and above the overdue banner.

        The two presses are the whole of this screen's job and neither is
        obvious: the buttons appear one at a time on each card, so nobody sees
        the pair and works out that they are a sequence. The labels are passed
        in rather than repeated in the sentence — see `tasks.help`.
      */}
      <HelpNote to="/uputstvo#my_tasks">
        {t('tasks.help', { start: t('tasks.action.start'), done: t('tasks.action.markDone') })}
      </HelpNote>

      {creating && <NewTaskModal onClose={closeNewTask} />}

      {overdue.length > 0 && (
        <Card className="border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/40">
          <p className="text-sm font-medium">
            {overdue.length === 1
              ? t('tasks.overdue.one')
              : t('tasks.overdue.many', { count: overdue.length })}
          </p>
        </Card>
      )}

      {query.isLoading && <Spinner />}
      {query.isError && (
        <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />
      )}

      {query.data && tasks.length === 0 && (
        <Card>
          <EmptyState
            icon={<ClipboardList className="size-8" />}
            title={openOnly ? t('tasks.empty.openTitle') : t('tasks.empty.allTitle')}
            description={canCreate ? t('tasks.empty.manager') : t('tasks.empty.worker')}
          />
        </Card>
      )}

      <ul className="space-y-2">
        {tasks.map((task) => (
          <TaskCard key={task.id} task={task} />
        ))}
      </ul>
    </div>
  )
}

/**
 * Assign work without first navigating to the report it came from.
 *
 * A task is **always** attached to a submission — that is the schema and it is
 * the right shape, because work with no recorded reason is work nobody can
 * close the loop on. So the submission is the first question here rather than a
 * detail, and the list is deliberately limited to open reports: assigning fresh
 * work against something already resolved is almost always a mistake.
 *
 * The equivalent form lives on each submission's own screen (`TaskSection`),
 * which is still the shortest path when you are already looking at the report.
 * This one exists for the other direction — planning a shift from the task list.
 */
function NewTaskModal({ onClose }: { onClose: () => void }) {
  const create = useCreateTaskOnSubmission()
  const people = useAssignableUsers()
  const t = useT()
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Open work only, newest first — see the note above.
  const submissions = useSubmissions({ status: OPEN_STATUSES, perPage: 100 })

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors({})

    const form = new FormData(event.currentTarget)

    try {
      await create.mutateAsync({
        submissionId: String(form.get('submissionId')),
        assignedToId: String(form.get('assignedToId')),
        title: String(form.get('title')),
        description: String(form.get('description') || '') || null,
        dueDate: String(form.get('dueDate') || '') || null,
      })

      toast.success(t('tasks.new.assigned'))
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        setErrors(
          Object.fromEntries(Object.entries(error.errors).map(([key, list]) => [key, list[0]])),
        )
        toast.error(error.message)
      } else {
        toast.error(t('tasks.new.failed'))
      }
    }
  }

  const rows = submissions.data?.data ?? []

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tasks.new')}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('tasks.new.cancel')}
          </Button>
          {/* Outside the <form>, so it submits by id. */}
          <Button
            type="submit"
            form="new-task"
            loading={create.isPending}
            disabled={rows.length === 0}
          >
            {t('tasks.new.submit')}
          </Button>
        </>
      }
    >
      <form id="new-task" onSubmit={handleSubmit} className="space-y-4">
        <Field
          label={t('tasks.new.submission')}
          required
          error={errors.submissionId}
          hint={t('tasks.new.submission.hint')}
        >
          <Select name="submissionId" required defaultValue="">
            <option value="" disabled>
              {submissions.isLoading ? t('state.loading') : t('tasks.new.submission.placeholder')}
            </option>
            {rows.map((submission) => (
              <option key={submission.id} value={submission.id}>
                {submission.referenceNo} — {submission.workCenter?.name}
                {submission.title ? ` — ${submission.title}` : ''}
              </option>
            ))}
          </Select>
        </Field>

        {submissions.data && rows.length === 0 && (
          <p className="muted text-sm">{t('tasks.new.submission.none')}</p>
        )}

        <Field label={t('tasks.new.title')} required error={errors.title}>
          <Input
            name="title"
            required
            maxLength={200}
            placeholder={t('tasks.new.title.placeholder')}
          />
        </Field>

        <Field label={t('tasks.new.assignee')} required error={errors.assignedToId}>
          <Select name="assignedToId" required defaultValue="">
            <option value="" disabled>
              {t('tasks.new.assignee.placeholder')}
            </option>
            {people.data?.data.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name} — {person.roleLabel}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('tasks.new.description')} error={errors.description}>
          <Textarea
            name="description"
            rows={2}
            placeholder={t('tasks.new.description.placeholder')}
          />
        </Field>

        <Field
          label={t('tasks.new.dueDate')}
          error={errors.dueDate}
          hint={t('tasks.new.dueDate.hint')}
        >
          {/* The company's clock, not UTC: toISOString() on a CEST evening
              yields tomorrow, which would reject today as a due date. */}
          <DateInput name="dueDate" min={todayISO()} />
        </Field>

      </form>
    </Modal>
  )
}

function TaskCard({ task }: { task: Task }) {
  const update = useUpdateTask()
  const t = useT()

  /*
   * This page lists the signed-in user's OWN tasks, so they are always the
   * assignee — which means verification is never theirs to do. Someone else
   * signs it off, which is the entire point of the step.
   */
  const next =
    task.status === 'OPEN'
      ? { status: 'IN_PROGRESS', label: t('tasks.action.start'), icon: Clock }
      : task.status === 'IN_PROGRESS'
        ? { status: 'DONE', label: t('tasks.action.markDone'), icon: CheckCircle2 }
        : null

  async function move(status: string) {
    try {
      await update.mutateAsync({ id: task.id, status })
      toast.success(t('tasks.updated'))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t('tasks.updateFailed'))
    }
  }

  return (
    <li className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{task.title}</p>
          <p className="muted mt-0.5 text-xs">
            {task.dueDate
              ? t('tasks.card.due', { date: formatDate(task.dueDate) })
              : t('tasks.card.noDue')}
            {' · '}
            <Link
              to={`/prijave/${task.submissionId}`}
              // Inline inside a sentence, so it cannot be given a height —
              // vertical padding widens the tap area without breaking the line.
              className="text-brand-600 inline-block py-2 underline"
            >
              {t('tasks.card.openSubmission')}
            </Link>
          </p>
        </div>

        <div className="flex shrink-0 gap-1.5">
          {task.isOverdue && <Badge color="rose">{t('tasks.card.overdue')}</Badge>}
          <Badge color={task.statusColor}>{task.statusLabel}</Badge>
        </div>
      </div>

      {task.description && (
        <p className="muted mt-2 text-sm whitespace-pre-wrap">{task.description}</p>
      )}

      {next && (
        <Button variant="secondary" onClick={() => move(next.status)} loading={update.isPending} className="mt-3">
          <next.icon className="size-4" aria-hidden />
          {next.label}
        </Button>
      )}
    </li>
  )
}
